import { useState } from 'react';
import { toast } from 'sonner';
import { Package, Plus, Trash2, Mail, Library } from 'lucide-react';
import MfrPicker from './MfrPicker';
import { openMfr } from './MfrDialog';
import { UNITS } from '../lib/catalog';
import { eur } from '../lib/license';
import { getMfr } from '../lib/mfr';
import { quoteMail } from '../lib/firePond';
import { useDealers, itemKey, dealerPrice, setDealerPrice } from '../lib/dealers';

const uid = () => `rm${Date.now().toString(36)}${Math.random().toString(36).slice(2, 5)}`;
const dec = (v) => (v === '' ? null : Number.isFinite(+String(v).replace(',', '.')) ? +String(v).replace(',', '.') : null);

function Dealer({ t, m, i, set, dl }) {
  const key = itemKey(m);
  const pick = (id) => { const d = dl.list.find((x) => x.id === id); const p = id ? dealerPrice(dl, key, id) : null; set({ dealer: id || null, dealerName: d?.name || '', ...(id ? { price: p } : {}) }); };
  if (!dl.list.length) return null;
  return (
    <select className="rm-dealer" data-testid={`rm-dealer-${i}`} value={m.dealer || ''} onChange={(e) => pick(e.target.value)}>
      <option value="">{t.dlNone}</option>
      {dl.list.map((d) => { const p = dealerPrice(dl, key, d.id); return <option key={d.id} value={d.id}>{d.name}{p != null ? ` · ${eur(p, t.locale)}` : ''}</option>; })}
    </select>
  );
}

function Row({ t, m, i, set, del, dl }) {
  const setPrice = (v) => { const price = dec(v); if (m.dealer) setDealerPrice(itemKey(m), m.dealer, price); set({ price }); };
  return (
    <tr data-testid={`rm-row-${i}`}>
      <td><textarea rows={1} data-testid={`rm-name-${i}`} value={m.name} onChange={(e) => set({ name: e.target.value })} /><small>{[m.mfr, m.dim, m.sku].filter(Boolean).join(' · ')}</small><Dealer t={t} m={m} i={i} set={set} dl={dl} /></td>
      <td><input className="rm-num" inputMode="decimal" data-testid={`rm-qty-${i}`} defaultValue={m.qty} onBlur={(e) => set({ qty: dec(e.target.value) ?? 0 })} /></td>
      <td><select data-testid={`rm-unit-${i}`} value={m.unit} onChange={(e) => set({ unit: e.target.value })}>{UNITS.map((u) => <option key={u} value={u}>{t['unit_' + u]}</option>)}</select></td>
      <td><input className="rm-num" inputMode="decimal" key={`${m.dealer || ''}${m.price}`} data-testid={`rm-price-${i}`} placeholder={t.mfOnReq} defaultValue={m.price ?? ''} onBlur={(e) => setPrice(e.target.value)} /></td>
      <td data-testid={`rm-amount-${i}`}>{m.price != null ? eur(m.price * (+m.qty || 0), t.locale) : '–'}</td>
      <td><button className="icon-btn danger" data-testid={`rm-del-${i}`} onClick={del}><Trash2 size={14} /></button></td>
    </tr>
  );
}

// manufacturer products of fittings placed on the map for this report
function mapProducts(project, report) {
  const g = {};
  (project?.fittings || []).filter((f) => f.prod && (f.reportId === report.id || (!f.reportId && project.reports.length === 1))).forEach((f) => {
    const k = `${f.prod.mfrId}|${f.prod.ref}`;
    g[k] = g[k] || { ...f.prod, unit: 'pcs', qty: 0 };
    g[k].qty += 1;
  });
  return Object.values(g);
}

export default function ReportMaterials({ t, project, report, updateReport }) {
  const [pick, setPick] = useState(false);
  const list = report.materials || [];
  const save = (next) => updateReport({ materials: next });
  const add = (m) => { save([...list, { id: uid(), qty: 1, unit: 'pcs', price: null, ...m }]); toast.success(t.rmAdded); };
  const fromItem = (x) => add({ ref: x.id, mfrId: x.mfrId, mfr: x.mfrId ? x.mfr : '', name: x.name, dim: x.dim || '', sku: x.sku || '', mat: x.mat || '', unit: x.unit || 'pcs', price: x.price ?? null, url: x.url || '' });
  const total = list.reduce((s, m) => s + (m.price != null ? m.price * (+m.qty || 0) : 0), 0);
  const dl = useDealers();
  const onMap = mapProducts(project, report);
  const dealers = dl.list.filter((d) => list.some((m) => m.dealer === d.id));
  const mfrs = [...new Set([...list, ...onMap].map((m) => m.mfrId).filter(Boolean))];
  return (
    <div className="rep-mat" data-testid="report-materials">
      <div className="sub-title"><Package size={14} /> {t.rmTitle}</div>
      {list.length ? (
        <div className="rm-wrap"><table className="mat-table rm-table" data-testid="rm-table">
          <thead><tr><th>{t.mfColName}</th><th>{t.rmQty}</th><th>{t.mfColUnit}</th><th>{t.mfPrice}</th><th>{t.colAmount}</th><th /></tr></thead>
          <tbody>{list.map((m, i) => <Row key={m.id} t={t} m={m} i={i} set={(p) => save(list.map((x) => (x.id === m.id ? { ...x, ...p } : x)))} del={() => save(list.filter((x) => x.id !== m.id))} dl={dl} />)}</tbody>
          <tfoot><tr><td colSpan={4}>{t.rmSum}</td><td data-testid="rm-total">{eur(total, t.locale)}</td><td /></tr></tfoot>
        </table></div>
      ) : <div className="empty" data-testid="rm-empty">{t.rmEmpty}</div>}
      {onMap.length > 0 && <div className="rm-map" data-testid="rm-map-products">🗺️ {t.rmOnMap}: {onMap.map((m) => `${m.qty}× ${m.name} ${m.dim} (${m.mfr})`).join(' · ')}</div>}
      <div className="row rm-actions">
        <button className="btn btn-dark btn-sm" data-testid="rm-pick-btn" onClick={() => setPick(!pick)}><Library size={14} /> {t.rmPick}</button>
        <button className="btn btn-outline btn-sm" data-testid="rm-add-btn" onClick={() => add({ name: t.mfOwn, dim: '' })}><Plus size={14} /> {t.rmAdd}</button>
        <button className="btn btn-outline btn-sm" data-testid="rm-manage-btn" onClick={() => openMfr()}>{t.rmManage}</button>
        {mfrs.map((id) => { const d = getMfr(id); return d && <button key={id} className="btn btn-proj btn-sm" title={t.rmQuoteHint} data-testid={`rm-quote-${id}`} onClick={() => quoteMail(t, d, [...list, ...onMap].filter((m) => m.mfrId === id && +m.qty > 0), report.title, t.rmQuote)}><Mail size={14} /> {t.rmQuote}: {d.name}</button>; })}
        {dealers.map((d) => <button key={d.id} className="btn btn-green btn-sm" title={t.dlOrderHint} data-testid={`rm-order-${d.id}`} onClick={() => quoteMail(t, d, list.filter((m) => m.dealer === d.id), report.title, t.dlOrder, t.dlMailHello)}><Mail size={14} /> {t.dlOrder}: {d.name}</button>)}
      </div>
      {pick && <MfrPicker t={t} testid="rm-picker" onPick={fromItem} onOwn={(q) => add({ name: q, dim: '' })} />}
    </div>
  );
}
